import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Grid, Alert, Button, TextField, InputAdornment,
    FormControl, Select, MenuItem, InputLabel, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination, Skeleton,
    Avatar, Tabs, Tab, Tooltip, IconButton,
} from '@mui/material';
import {
    Search as SearchIcon,
    Report as ReportIcon,
    PendingActions as PendingIcon,
    Schedule as ScheduleIcon,
    Gavel as GavelIcon,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { adminApi, ReportsResponse, DashboardStats } from '../../api/adminApi';
import AdminSidebar from './components/AdminSidebar';
import KPIStatCard from './components/KPIStatCard';
import { adminTokens as T } from '../../theme/adminTokens';

interface Report {
    id: string;
    report_type: string;
    reporter_email: string;
    reported_user_email: string;
    created_at: string;
    priority: string;
    status: string;
    content?: string | null;
    category_description: string;
    total_reports_against_user: number;
}

const DRAWER_WIDTH = 260;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const PRIORITY_TONE: Record<string, { dot: string; bg: string; text: string; label: string }> = {
    urgent: { dot: T.color.error.base,   bg: T.color.error.soft,   text: T.color.error.text,   label: 'Urgent' },
    high:   { dot: T.color.warning.base, bg: T.color.warning.soft, text: T.color.warning.text, label: 'High' },
    medium: { dot: T.color.info.base,    bg: T.color.info.soft,    text: T.color.info.text,    label: 'Medium' },
    low:    { dot: T.color.success.base, bg: T.color.success.soft, text: T.color.success.text, label: 'Low' },
};
const STATUS_TONE: Record<string, { bg: string; text: string; label: string }> = {
    pending:   { bg: T.color.warning.soft, text: T.color.warning.text, label: 'Pending' },
    reviewing: { bg: T.color.info.soft,    text: T.color.info.text,    label: 'Reviewing' },
    resolved:  { bg: T.color.success.soft, text: T.color.success.text, label: 'Resolved' },
    dismissed: { bg: T.color.surface.sunken, text: T.color.text.secondary, label: 'Dismissed' },
};

const formatReportType = (type: string) =>
    type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const initialsFrom = (email: string | null | undefined) => {
    if (!email) return '?';
    const parts = email.split('@')[0].split(/[._-]/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

const formatTimeAgo = (dateString: string) => {
    const ms = Date.now() - new Date(dateString).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
};

const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
    const t = PRIORITY_TONE[priority?.toLowerCase()] || PRIORITY_TONE.medium;
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: `${T.space[1]}px`,
            px: `${T.space[2]}px`, py: `2px`,
            borderRadius: `${T.radius.pill}px`,
            bgcolor: t.bg, color: t.text,
            fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
        }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: t.dot }} />
            {t.label}
        </Box>
    );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const t = STATUS_TONE[status?.toLowerCase()] || STATUS_TONE.pending;
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center',
            px: `${T.space[2]}px`, py: `2px`,
            borderRadius: `${T.radius.pill}px`,
            bgcolor: t.bg, color: t.text,
            fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
        }}>{t.label}</Box>
    );
};

const WaitingBadge: React.FC<{ createdAt: string; status: string }> = ({ createdAt, status }) => {
    if (status?.toLowerCase() !== 'pending' && status?.toLowerCase() !== 'reviewing') return null;
    const hours = (Date.now() - new Date(createdAt).getTime()) / 3600000;
    let tone: 'neutral' | 'warning' | 'error' = 'neutral';
    if (hours > 48) tone = 'error';
    else if (hours > 12) tone = 'warning';
    if (tone === 'neutral') return null;
    const palette = tone === 'error' ? { bg: T.color.error.soft, text: T.color.error.text }
                                     : { bg: T.color.warning.soft, text: T.color.warning.text };
    return (
        <Tooltip title={tone === 'error' ? 'Open more than 48 hours' : 'Open more than 12 hours'}>
            <Box sx={{
                display: 'inline-flex', alignItems: 'center',
                px: `${T.space[2]}px`, py: `2px`, ml: `${T.space[2]}px`,
                borderRadius: `${T.radius.pill}px`,
                bgcolor: palette.bg, color: palette.text,
                fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
                fontVariantNumeric: 'tabular-nums',
            }}>
                {hours < 24 ? `${Math.floor(hours)}h` : `${Math.floor(hours / 24)}d`} waiting
            </Box>
        </Tooltip>
    );
};

const ReportsListScreen: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [reports, setReports] = useState<Report[]>([]);
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');

    useEffect(() => {
        let routeStatus = 'all';
        if (location.pathname.includes('/reports/pending')) routeStatus = 'pending';
        else if (location.pathname.includes('/reports/resolved')) routeStatus = 'resolved';
        else if (location.pathname.includes('/reports/dismissed')) routeStatus = 'dismissed';

        setStatusFilter(searchParams.get('status') || routeStatus);
        setPriorityFilter(searchParams.get('priority') || 'all');
        setTypeFilter(searchParams.get('type') || 'all');
        setSearchQuery(searchParams.get('search') || '');
        setPage(parseInt(searchParams.get('page') || '0'));
    }, [searchParams, location.pathname]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setStatsLoading(true);
                const r = await adminApi.getDashboardStats();
                if (active) setStats(r.data);
            } catch {
                if (active) setStats(null);
            } finally {
                if (active) setStatsLoading(false);
            }
        })();
        return () => { active = false; };
    }, []);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const response: ReportsResponse = await adminApi.getReports({
                    page,
                    limit: rowsPerPage,
                    status: statusFilter !== 'all' ? statusFilter : undefined,
                    priority: priorityFilter !== 'all' ? priorityFilter : undefined,
                    type: typeFilter !== 'all' ? typeFilter : undefined,
                    search: searchQuery.trim() || undefined,
                });
                if (!active) return;
                setReports(response.data || []);
                setTotalCount(response.pagination.total);
            } catch (err: any) {
                if (active) setError(err.message || 'Failed to load reports');
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [page, rowsPerPage, searchQuery, statusFilter, priorityFilter, typeFilter]);

    const updateURLParams = (params: Record<string, string>) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(params).forEach(([key, value]) => {
            if (value && value !== 'all' && value !== '0') next.set(key, value);
            else next.delete(key);
        });
        setSearchParams(next);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setPage(0);
        updateURLParams({ search: e.target.value, page: '0' });
    };
    const handleStatusTabChange = (_: React.SyntheticEvent, value: string) => {
        setStatusFilter(value); setPage(0); updateURLParams({ status: value, page: '0' });
    };
    const handlePriorityFilterChange = (e: any) => {
        setPriorityFilter(e.target.value); setPage(0); updateURLParams({ priority: e.target.value, page: '0' });
    };
    const handleTypeFilterChange = (e: any) => {
        setTypeFilter(e.target.value); setPage(0); updateURLParams({ type: e.target.value, page: '0' });
    };

    const tabCounts = useMemo(() => ({
        pending: stats?.pendingReports,
        resolved: stats?.resolvedReports,
        dismissed: stats?.dismissedReports,
    }), [stats]);

    return (
        <Box sx={{ display: 'flex', bgcolor: T.color.surface.page, minHeight: '100vh' }}>
            <AdminSidebar pendingReportsCount={stats?.pendingReports ?? 0} />
            <Box component="main" sx={{
                flexGrow: 1,
                p: { xs: `${T.space[4]}px`, md: `${T.space[6]}px` },
                minWidth: 0,
                overflow: 'hidden',
            }}>
                {/* Page header */}
                <Box sx={{ mb: `${T.space[6]}px` }}>
                    <Typography sx={{
                        fontSize: T.type.size['2xl'], fontWeight: T.type.weight.bold,
                        color: T.color.text.primary, lineHeight: T.type.lineHeight.tight,
                    }}>Moderation queue</Typography>
                    <Typography sx={{ fontSize: T.type.size.md, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
                        Review reports, triage by priority, and resolve safety issues.
                    </Typography>
                </Box>

                {/* Safety KPIs */}
                <Grid container spacing={3} sx={{ mb: `${T.space[5]}px` }}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Open reports"
                            value={stats?.pendingReports}
                            icon={<PendingIcon />}
                            tone={(stats?.pendingReports ?? 0) > 20 ? 'error' : 'warning'}
                            loading={statsLoading}
                            onClick={() => handleStatusTabChange({} as any, 'pending')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Urgent priority"
                            value={stats?.reportsByPriority?.urgent}
                            icon={<ReportIcon />}
                            tone="error"
                            loading={statsLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Avg. response time"
                            value={stats?.avgResponseTimeHours}
                            format={(v) => `${v}h`}
                            icon={<ScheduleIcon />}
                            tone="neutral"
                            loading={statsLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Bans this week"
                            value={stats?.statusTrends?.bansThisWeek}
                            icon={<GavelIcon />}
                            tone="error"
                            loading={statsLoading}
                        />
                    </Grid>
                </Grid>

                {/* Tabs */}
                <Box sx={{
                    bgcolor: T.color.surface.raised,
                    border: `1px solid ${T.color.surface.border}`,
                    borderRadius: `${T.radius.lg}px`,
                    boxShadow: T.shadow.sm,
                    mb: `${T.space[4]}px`,
                }}>
                    <Tabs
                        value={statusFilter}
                        onChange={handleStatusTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            px: `${T.space[3]}px`,
                            minHeight: 48,
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontSize: T.type.size.sm,
                                fontWeight: T.type.weight.semibold,
                                color: T.color.text.secondary,
                                minHeight: 48,
                            },
                            '& .Mui-selected': { color: `${T.color.brand[600]} !important` },
                            '& .MuiTabs-indicator': { backgroundColor: T.color.brand[500] },
                        }}
                    >
                        <Tab label="All" value="all" />
                        <Tab label={`Pending${tabCounts.pending != null ? ` · ${tabCounts.pending}` : ''}`} value="pending" />
                        <Tab label="Reviewing" value="reviewing" />
                        <Tab label={`Resolved${tabCounts.resolved != null ? ` · ${tabCounts.resolved}` : ''}`} value="resolved" />
                        <Tab label={`Dismissed${tabCounts.dismissed != null ? ` · ${tabCounts.dismissed}` : ''}`} value="dismissed" />
                    </Tabs>
                </Box>

                {/* Filter bar */}
                <Box sx={{
                    bgcolor: T.color.surface.raised,
                    border: `1px solid ${T.color.surface.border}`,
                    borderRadius: `${T.radius.lg}px`,
                    p: `${T.space[4]}px`,
                    mb: `${T.space[4]}px`,
                    boxShadow: T.shadow.sm,
                    display: 'flex', flexWrap: 'wrap', gap: `${T.space[3]}px`, alignItems: 'center',
                }}>
                    <TextField
                        placeholder="Search reports…"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        size="small"
                        sx={{ minWidth: 260, flex: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: T.color.text.tertiary, fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Priority</InputLabel>
                        <Select value={priorityFilter} label="Priority" onChange={handlePriorityFilterChange}>
                            <MenuItem value="all">All priorities</MenuItem>
                            <MenuItem value="urgent">Urgent</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="low">Low</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Type</InputLabel>
                        <Select value={typeFilter} label="Type" onChange={handleTypeFilterChange}>
                            <MenuItem value="all">All types</MenuItem>
                            <MenuItem value="fake_profile">Fake profile</MenuItem>
                            <MenuItem value="inappropriate_photos">Inappropriate photos</MenuItem>
                            <MenuItem value="harassment">Harassment</MenuItem>
                            <MenuItem value="spam_promotional">Spam</MenuItem>
                            <MenuItem value="underage_user">Underage user</MenuItem>
                            <MenuItem value="hate_speech">Hate speech</MenuItem>
                            <MenuItem value="impersonation">Impersonation</MenuItem>
                            <MenuItem value="scam_fraud">Scam / fraud</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: `${T.space[4]}px`, borderRadius: `${T.radius.md}px` }}
                        action={<Button color="inherit" size="small" onClick={() => updateURLParams({ _t: String(Date.now()) })}>Retry</Button>}
                    >
                        {error}
                    </Alert>
                )}

                {/* Reports table */}
                <Box sx={{
                    bgcolor: T.color.surface.raised,
                    border: `1px solid ${T.color.surface.border}`,
                    borderRadius: `${T.radius.lg}px`,
                    boxShadow: T.shadow.sm,
                    overflow: 'hidden',
                }}>
                    <TableContainer>
                        <Table size="medium">
                            <TableHead>
                                <TableRow sx={{ '& th': { bgcolor: T.color.surface.sunken, color: T.color.text.secondary, fontWeight: T.type.weight.semibold, fontSize: T.type.size.xs, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${T.color.surface.border}` } }}>
                                    <TableCell>Reason</TableCell>
                                    <TableCell>Reporter</TableCell>
                                    <TableCell>Reported user</TableCell>
                                    <TableCell>Priority</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">History</TableCell>
                                    <TableCell>Opened</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: 6 }).map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={8}><Skeleton variant="text" height={32} /></TableCell>
                                        </TableRow>
                                    ))
                                ) : reports.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: `${T.space[6]}px`, color: T.color.text.tertiary }}>
                                            No reports match the current filters.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    reports.map((report) => (
                                        <TableRow
                                            key={report.id}
                                            hover
                                            onClick={() => navigate(`/admin/reports/${report.id}`)}
                                            sx={{
                                                cursor: 'pointer',
                                                '& td': { borderBottom: `1px solid ${T.color.surface.border}`, fontSize: T.type.size.sm, verticalAlign: 'top', py: `${T.space[3]}px` },
                                                '&:hover td': { bgcolor: T.color.surface.sunken },
                                            }}
                                        >
                                            <TableCell sx={{ maxWidth: 320 }}>
                                                <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.text.primary }}>
                                                    {formatReportType(report.report_type)}
                                                </Typography>
                                                {report.content && (
                                                    <Typography sx={{
                                                        fontSize: T.type.size.xs, color: T.color.text.secondary,
                                                        mt: `${T.space[1]}px`,
                                                        overflow: 'hidden', textOverflow: 'ellipsis',
                                                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                                    }}>
                                                        {report.content}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[2]}px` }}>
                                                    <Avatar sx={{ width: 28, height: 28, bgcolor: T.color.info.soft, color: T.color.info.text, fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold }}>
                                                        {initialsFrom(report.reporter_email)}
                                                    </Avatar>
                                                    <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                                                        {report.reporter_email || 'Unknown'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[2]}px` }}>
                                                    <Avatar sx={{ width: 28, height: 28, bgcolor: T.color.error.soft, color: T.color.error.text, fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold }}>
                                                        {initialsFrom(report.reported_user_email)}
                                                    </Avatar>
                                                    <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                                                        {report.reported_user_email || 'Unknown'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell><PriorityBadge priority={report.priority} /></TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <StatusBadge status={report.status} />
                                                    <WaitingBadge createdAt={report.created_at} status={report.status} />
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                color: report.total_reports_against_user > 3 ? T.color.error.text : T.color.text.secondary,
                                                fontWeight: report.total_reports_against_user > 3 ? T.type.weight.bold : T.type.weight.regular,
                                                fontVariantNumeric: 'tabular-nums',
                                            }}>
                                                {report.total_reports_against_user > 1
                                                    ? `${report.total_reports_against_user}×`
                                                    : '—'}
                                            </TableCell>
                                            <TableCell sx={{ color: T.color.text.secondary, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                {formatTimeAgo(report.created_at)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Open report"
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/admin/reports/${report.id}`); }}
                                                    sx={{ color: T.color.text.tertiary, '&:hover': { color: T.color.brand[500] } }}
                                                >
                                                    <ArrowForwardIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default ReportsListScreen;
